import { StepperLayout, StepperLayoutStep, useSnackbar } from "@homebound/beam";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PayTradesPublishStep } from "src/routes/projects/dynamic-schedules/draft-mode/PayTradesPublishStep";
import { usePublishDraftScheduleStepperQuery } from "src/routes/projects/dynamic-schedules/draft-mode/PublishDraftScheduleStepper.generated";
import {
  ScheduleDraftMode_NewlyCompletedPlanTaskFragment,
  ScheduleDraftMode_PlanTaskFragment,
  useSaveDraftPlanScheduleMutation,
} from "src/routes/projects/dynamic-schedules/draft-mode/ScheduleDraftMode.generated";
import { useDraftScheduleStore } from "src/routes/projects/dynamic-schedules/draft-mode/scheduleDraftStore";
import { ProjectParams } from "src/routes/routesDef";
import { createDraftScheduleUrl } from "src/RouteUrls";
import { foldEnum, queryResult } from "src/utils";
import { DelayFlagPublishStep, maybeGetTaskToDelay } from "./DelayFlagPublishStep";
import { useDraftPlanSchedule } from "./DraftScheduleLayout";
import { TradeConfirmPublishStep } from "./TradeConfirmPublishStep";

/** The publish route. It reads the draft that `DraftScheduleLayout` shares with the draft schedule page. */
export function PublishDraftSchedulePage() {
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useDraftPlanSchedule();
  return queryResult(query, ({ draftPlanSchedule }) => (
    <PublishDraftScheduleStepper
      scheduleParentId={projectId}
      draftTasks={draftPlanSchedule.planTasks}
      newlyCompletedPlanTasks={draftPlanSchedule.newlyCompletedPlanTasks}
    />
  ));
}

type PublishDraftScheduleStepperProps = {
  scheduleParentId: string;
  draftTasks: ScheduleDraftMode_PlanTaskFragment[];
  newlyCompletedPlanTasks: ScheduleDraftMode_NewlyCompletedPlanTaskFragment[];
};

export enum PublishDraftScheduleStep {
  DelayFlags = "DelayFlags",
  PayTrades = "PayTrades",
  TradeConfirm = "TradeConfirm",
}

export function PublishDraftScheduleStepper(props: PublishDraftScheduleStepperProps) {
  const { scheduleParentId, draftTasks, newlyCompletedPlanTasks } = props;
  const showTradeComms = useFeatureFlag(FeatureFlagType.DynamicSchedulesTradeComms);
  const { triggerNotice } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const showClickToPayFlag = !newlyCompletedPlanTasks.every((ncpt) => ncpt.protoBills.isEmpty);
  const stepValues = [
    PublishDraftScheduleStep.DelayFlags,
    ...(showClickToPayFlag ? [PublishDraftScheduleStep.PayTrades] : []),
    ...(showTradeComms ? [PublishDraftScheduleStep.TradeConfirm] : []),
  ];

  const { data } = usePublishDraftScheduleStepperQuery({ variables: { scheduleParentId } });
  const [saveDraftSchedule] = useSaveDraftPlanScheduleMutation();

  const scheduleParentName = useDraftScheduleStore((state) => state.scheduleParentName);
  const getTasksWithDateOrDurationChanges = useDraftScheduleStore((state) => state.getTasksWithDateOrDurationChanges);
  const draftTaskChanges = useDraftScheduleStore((state) => state.draftTaskChanges);
  const requiredScheduleFlags = useDraftScheduleStore((state) => state.requiredScheduleFlags);
  const userAddedScheduleFlags = useDraftScheduleStore((state) => state.userAddedScheduleFlags);
  const draftTaskTradeC2PInput = useDraftScheduleStore((state) => state.draftTaskTradeC2PInput);
  const tparAvailabilityRequestInput = useDraftScheduleStore((state) => state.tparAvailabilityRequestInput);
  const skipStaleTaskAutoPin = useDraftScheduleStore((state) => state.skipStaleTaskAutoPin);
  const currentDate = useDraftScheduleStore((state) => state.currentDate);
  const removeUserAddedScheduleFlags = useDraftScheduleStore((state) => state.removeUserAddedScheduleFlags);
  const reset = useDraftScheduleStore((state) => state.reset);

  // Each step reports whether it's complete. The delay flag step starts complete when no task needs a flag.
  const [completedSteps, setCompletedSteps] = useState<Partial<Record<PublishDraftScheduleStep, boolean>>>(() => ({
    [PublishDraftScheduleStep.DelayFlags]: !maybeGetTaskToDelay(getTasksWithDateOrDurationChanges(draftTasks)),
  }));
  const updateStepStatus = useCallback((completed: boolean, step: PublishDraftScheduleStep) => {
    // Skip unchanged values so the steps' effects don't re-render the stepper forever
    setCompletedSteps((prev) => (prev[step] === completed ? prev : { ...prev, [step]: completed }));
  }, []);

  async function onPublish() {
    // Notify Trades lists every changed task. Only the ones with a selected contact get a notification.
    const withTradeUpdates =
      showTradeComms && tparAvailabilityRequestInput.some((request) => request.tradePartnerContactIds?.nonEmpty);
    await saveDraftSchedule({
      variables: {
        input: {
          scheduleParentId,
          draftTaskChanges: [
            ...draftTaskChanges,
            ...requiredScheduleFlags,
            // remove unnecessary fields from sending to the backend, merge the user added flags with draft changes
            ...userAddedScheduleFlags.map(({ taskId, taskName, title, scheduleFlagReasonType, ...others }) => ({
              id: taskId,
              scheduleFlags: [{ ...others }],
            })),
          ],
          ...draftTaskTradeC2PInput,
          ...(withTradeUpdates && { tradePartnerAvailabilityRequests: tparAvailabilityRequestInput }),
          skipStaleTaskAutoPin,
          currentDate,
        },
      },
    });
    reset();
    userAddedScheduleFlags.forEach((flag) => {
      if (flag.clientId) {
        removeUserAddedScheduleFlags(flag.clientId);
      }
    });
    navigate(createDraftScheduleUrl(scheduleParentId));
    triggerNotice({ message: "Draft schedule changes have been saved" });
    if (withTradeUpdates) {
      triggerNotice({ message: "Scheduling notifications have been sent to Trade Partners." });
    }
  }

  // Cancelling keeps the draft. Go back so the user returns to the view they came from. A link opened in a new tab
  // has nothing to go back to, so it opens the draft schedule instead.
  function onCancel() {
    if (location.key === "default") {
      navigate(createDraftScheduleUrl(scheduleParentId), { replace: true });
    } else {
      navigate(-1);
    }
  }

  const steps = stepValues.map((step, i): StepperLayoutStep => {
    const complete = !!completedSteps[step];
    return {
      label: stepLabels[step],
      // A step opens once every step before it is complete
      disabled: stepValues.slice(0, i).some((previous) => !completedSteps[previous]),
      primaryDisabled:
        !complete &&
        (step === PublishDraftScheduleStep.TradeConfirm
          ? (data?.planSchedule.canSendTradePartnerAvailabilityRequestEmail.disabledReasons.first?.message ?? true)
          : true),
      content: foldEnum(step, {
        [PublishDraftScheduleStep.DelayFlags]: (
          <DelayFlagPublishStep draftTasks={draftTasks} updateStepStatus={updateStepStatus} />
        ),
        [PublishDraftScheduleStep.PayTrades]: (
          <PayTradesPublishStep newlyCompletedPlanTasks={newlyCompletedPlanTasks} updateStepStatus={updateStepStatus} />
        ),
        [PublishDraftScheduleStep.TradeConfirm]: <TradeConfirmPublishStep updateStepStatus={updateStepStatus} />,
      }),
    };
  });

  return (
    <StepperLayout
      title="Publish Schedule"
      documentTitleSuffix={scheduleParentName}
      steps={steps}
      onCancel={onCancel}
      completeLabel="Publish"
      onComplete={onPublish}
    />
  );
}

const stepLabels: Record<PublishDraftScheduleStep, string> = {
  [PublishDraftScheduleStep.DelayFlags]: "Add Delay Flags",
  [PublishDraftScheduleStep.PayTrades]: "Pay Trades",
  [PublishDraftScheduleStep.TradeConfirm]: "Notify Trades",
};
